import React from 'react';
import { tFor } from '../../i18n/i18n.js';
import { toMs, formatMMSS } from './countdownHelpers.js';

// CountdownTimer — Ticker for a single reservation.
//
// Pure presentational component. Ticks every second; renders MM:SS.
// Fires `onExpire` exactly once when the clock hits zero, then clears
// the interval (the parent should unmount us at that point).
//
// `expiresAt` is an ISO8601 string (the server-set booking.expiresAt).
// We treat it as wall-clock UTC internally — no Date parsing tricks —
// because the server already gave us a UTC instant.

export function CountdownTimer({
  expiresAt,
  onExpire,
  locale,
  labelKey = 'pay.timeRemaining',
  expiredKey = 'pay.expiredMessage',
}) {
  const t = tFor(locale);
  const targetMs = React.useMemo(() => toMs(expiresAt), [expiresAt]);

  // Compute remaining ms from a target timestamp. We deliberately read
  // Date.now() fresh on each tick (state), not on each render, so the
  // label stays correct without forcing the parent to re-render.
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  // Guards against firing onExpire twice. The parent normally unmounts
  // us on the first callback, but defensively we still dedupe so a
  // slow re-render can't fire it twice.
  const firedRef = React.useRef(false);

  React.useEffect(() => {
    if (!Number.isFinite(targetMs)) return undefined;
    // Already past expiry — fire immediately next tick.
    if (targetMs <= Date.now()) {
      if (!firedRef.current) {
        firedRef.current = true;
        onExpire?.();
      }
      return undefined;
    }
    const id = setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (targetMs <= now && !firedRef.current) {
        firedRef.current = true;
        clearInterval(id);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [targetMs, onExpire]);

  // No expiresAt — render nothing (the parent decides what fallback UI
  // to show, e.g. a "submit anyway" path).
  if (!Number.isFinite(targetMs)) return null;

  const msLeft = targetMs - nowMs;
  const isExpired = msLeft <= 0;

  return (
    <div
      className={'sp-countdown' + (isExpired ? ' is-expired' : '')}
      role={isExpired ? 'alert' : 'status'}
      aria-live={isExpired ? 'assertive' : 'polite'}
    >
      {isExpired
        ? t(expiredKey)
        : t(labelKey).replace('{time}', formatMMSS(msLeft))}
    </div>
  );
}

export default CountdownTimer;
